import LinkButton from '@carvana/ds-link-button';
import SelectableTile from '@carvana/ds-selectable-tile';
import cx from 'classnames';
import { useContext, useState } from 'react';
import AutoSelectContext from '@/new-cars/AutoSelect';
import tileStyles from '@/new-cars/common/SelectableTile.module.css';
import VehicleMakeIcon from '~/components/common/VehicleMakeIcon';
import type { CustomizePackageOption } from '~/contracts/new-cars';
import formatAndDropDecimal from '~/utilities/formatAndDropDecimal';
import { isFallbackImage } from '~/utilities/isFallbackImage';
import optimizeImage from '~/utilities/optimizeImage';
import {
  Content,
  ImageArea,
  ImagePlaceholder,
  ImageUnavailable,
  OptionName,
  PriceAdd,
  StockCount
} from './PackageOptionCard.styles';

type OptionImageProps = {
  code: string;
  imageUrl: string;
  isFallback: boolean;
  isImageBroken: boolean;
  make: string;
  name: string;
  onImageError: () => void;
};

function OptionImage({
  code,
  imageUrl,
  isFallback,
  isImageBroken,
  make,
  name,
  onImageError
}: OptionImageProps) {
  if (imageUrl && !isImageBroken) {
    return (
      <img
        alt={name}
        className={cx(
          'h-full w-full',
          isFallback ? 'object-contain' : 'object-cover'
        )}
        onError={onImageError}
        src={imageUrl}
      />
    );
  }

  if (code === 'NONE') {
    return (
      <ImagePlaceholder>
        <VehicleMakeIcon aria-hidden="true" make={make} size={80} />
      </ImagePlaceholder>
    );
  }

  return <ImageUnavailable>Image unavailable</ImageUnavailable>;
}

type OwnProps = {
  isGreyedOut?: boolean;
  make: string;
  onDetailsClick: (option: CustomizePackageOption) => void;
  option: CustomizePackageOption;
  selected: boolean;
};

export default function PackageOptionCard({
  isGreyedOut = false,
  make,
  onDetailsClick,
  option,
  selected
}: OwnProps) {
  const { setPendingOptionDeselection, setPendingOptionSelection } =
    useContext(AutoSelectContext);
  const [isImageBroken, setIsImageBroken] = useState(false);
  const { count, code, description, imageUrl, name, priceAdd } = option;
  const isFallback = isFallbackImage(imageUrl);
  const optimizedImageUrl = imageUrl
    ? optimizeImage(imageUrl, { format: 'auto', height: 300, optimize: 'high' })
    : '';
  const hasDetails = description.split('\n').filter(Boolean).length > 0;

  return (
    <SelectableTile
      className={cx(tileStyles.tile, { 'opacity-50': isGreyedOut })}
      data-analytics-attributes={JSON.stringify({
        level: 'Options',
        section: 'Packages',
        text: name,
        tileAction: selected ? 'deselected' : 'selected'
      })}
      data-analytics-id="New Cars"
      data-testid={`package-option-card-${code}`}
      onClick={() => {
        if (selected) {
          setPendingOptionDeselection('package', code);
        } else {
          setPendingOptionSelection('package', code);
        }
      }}
      selected={selected}
      value={code}
    >
      <ImageArea className={isFallback ? 'bg-background-weak' : ''}>
        <OptionImage
          code={code}
          imageUrl={optimizedImageUrl}
          isFallback={isFallback}
          isImageBroken={isImageBroken}
          make={make}
          name={name}
          onImageError={() => setIsImageBroken(true)}
        />
      </ImageArea>
      <Content>
        <div className="flex flex-col gap-md">
          <OptionName>{name}</OptionName>
          {hasDetails && (
            <LinkButton
              className="w-fit"
              data-analytics-attributes={JSON.stringify({
                level: 'Options',
                section: 'Packages',
                text: 'Details'
              })}
              data-analytics-id="New Cars"
              data-testid="package-option-card-details-button"
              onClick={ev => {
                ev.stopPropagation();
                onDetailsClick(option);
              }}
              size="sm"
              underline={false}
              variant="default"
            >
              Details
            </LinkButton>
          )}
        </div>
        <PriceAdd>+${formatAndDropDecimal(priceAdd)}</PriceAdd>
        {count > 0 && <StockCount>{count} in stock</StockCount>}
      </Content>
    </SelectableTile>
  );
}
